import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

/** Purpose: verify an email address and complete the returned session. Why: account access may be gated on ownership confirmation. */
export default function VerifyEmail() {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [email, setEmail] = useState('');
  const [devOTP, setDevOTP] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, completeVerification } = useAuth();

  React.useEffect(() => {
    if (token && user) {
      navigate(
        user.isSuperAdmin === true
          ? '/admin'
          : user.role === 'AGENT'
            ? '/inbox'
            : '/analytics',
        { replace: true }
      );
    }
  }, [token, user, navigate]);

  React.useEffect(() => {
    // Get email from location state or query params
    const emailFromState = location.state?.email || new URLSearchParams(location.search).get('email');
    if (emailFromState) {
      setEmail(emailFromState);
    }

    if (location.state?.devOTP) {
      setDevOTP(location.state.devOTP);
      setSuccessMessage(
        'Local development mode: use the OTP shown below.'
      );
    } else if (location.state?.otpDelivery === 'console') {
      setSuccessMessage('Email service is not configured on the server. Check the backend console for the OTP.');
    }
  }, [location]);

  /** Purpose: Handle handle verify in the verify email module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleVerify = async (e) => {
    e.preventDefault();
    if (!otp || !email) {
      setError('OTP and email are required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await axios.post('/api/v1/auth/verify-email', {
        email,
        otp,
      });

      // The verification endpoint returns a valid session. Store it in the
      // AuthContext before navigating so protected routes stay authenticated.
      if (response.data.token) {
        completeVerification(response.data);
      }

      // Redirect based on role
      navigate(
        response.data.user?.isSuperAdmin === true
          ? '/admin'
          : response.data.user?.role === 'AGENT'
            ? '/inbox'
            : '/analytics',
        { replace: true }
      );
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /** Purpose: Handle handle resend otp in the verify email module. Why: Keeping this action in one handler makes its state or request update consistent. */
  const handleResendOTP = async () => {
    if (!email) {
      setError('Email is required');
      return;
    }

    setResending(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await axios.post('/api/v1/auth/resend-verification', { email });
      setError('');
      if (response.data?.devOTP) {
        setDevOTP(response.data.devOTP);
        setSuccessMessage('A new local development OTP has been created.');
      } else if (response.data?.otpDelivery === 'console') {
        setSuccessMessage('OTP resend ho gaya hai. Backend console me logged OTP check karo.');
      } else {
        setSuccessMessage('Verification OTP has been resent to your email!');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend OTP');
    } finally {
      setResending(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Verify Your Email
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Enter the OTP sent to your email address
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          {devOTP && (
            <Box
              sx={{
                mb: 2.5,
                p: 2,
                textAlign: 'center',
                border: '1px dashed',
                borderColor: 'primary.main',
                borderRadius: 2,
                bgcolor: 'rgba(25, 118, 210, 0.06)',
              }}
            >
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, letterSpacing: '0.12em', color: 'primary.main' }}>
                LOCAL DEVELOPMENT OTP
              </Typography>
              <Typography variant="h4" sx={{ mt: 0.75, fontWeight: 800, letterSpacing: '0.25em', fontVariantNumeric: 'tabular-nums' }}>
                {devOTP}
              </Typography>
              <Button size="small" onClick={() => setOtp(devOTP)} sx={{ mt: 0.75, textTransform: 'none' }}>
                Use this OTP
              </Button>
            </Box>
          )}

          <form onSubmit={handleVerify}>
            <TextField
              fullWidth
              type="email"
              label="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              margin="normal"
              disabled={!!location.state?.email}
            />

            <TextField
              fullWidth
              label="Verification OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              required
              margin="normal"
              inputProps={{ maxLength: 6, pattern: '[0-9]*' }}
              placeholder="Enter 6-digit OTP"
              helperText={devOTP ? 'Use the local development OTP shown above.' : 'Enter the 6-digit OTP sent to your email'}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading || !otp || !email}
            >
              {loading ? <CircularProgress size={24} /> : 'Verify Email'}
            </Button>

            <Box textAlign="center">
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={handleResendOTP}
                disabled={resending || !email}
              >
                {resending ? 'Resending...' : "Didn't receive OTP? Resend"}
              </Link>
            </Box>

            <Box textAlign="center" mt={2}>
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={() => navigate('/login')}
              >
                Back to Login
              </Link>
            </Box>
          </form>
        </Paper>
      </Box>
    </Container>
  );
}
